import { fetchInvoiceVerificationUrl, invoiceXmlUrl } from './api';

/**
 * Generates the invoice PDF client-side with the official KSeF generator
 * (`@akmf/ksef-fe-invoice-converter`, vendored under `vendor/`) and triggers a download. The output
 * matches the KSeF webapp PDF byte-for-byte. Fetches the stored invoice XML, hands it to the
 * generator, and saves the resulting blob as `{ksefNumber}.pdf`.
 *
 * Rejects if the XML can't be fetched or the form version is unsupported (the generator throws on an
 * unknown `kodSystemowy`), so the caller can show an error instead of downloading a blank file.
 *
 * `onResponse` fires once the invoice XML has been fetched, before the (CPU-bound) PDF render — letting
 * the caller stop a loading indicator while keeping the action otherwise in progress.
 *
 * The KSeF "KOD I" verification URL is fetched on demand (in parallel with the XML) and passed as
 * `qrCode`, so the generator renders the QR labelled with the KSeF number (online invoice). We never
 * set `qr2Code` — that is the offline KOD II, which would relabel KOD I as "offline". The fetch is
 * best-effort: if it yields nothing the PDF is still produced, just without the QR.
 */
export async function generateInvoicePdf(
  ksefNumber: string,
  onResponse?: () => void,
): Promise<void> {
  const [res, verificationUrl] = await Promise.all([
    fetch(invoiceXmlUrl(ksefNumber), { credentials: 'same-origin' }),
    fetchInvoiceVerificationUrl(ksefNumber),
  ]);
  if (!res.ok) {
    throw new Error(`invoice xml fetch failed: ${res.status}`);
  }
  const xml = await res.blob();
  onResponse?.();

  // Loaded on demand: the generator bundles pdfmake + fonts (~1.5 MB), kept out of the initial bundle.
  const { generateInvoice } = await import('@akmf/ksef-fe-invoice-converter');
  const file = new File([xml], `${ksefNumber}.xml`, { type: 'application/xml' });
  const pdf = await generateInvoice(
    file,
    { nrKSeF: ksefNumber, qrCode: verificationUrl ?? undefined },
    'blob',
  );

  const url = URL.createObjectURL(pdf);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${ksefNumber}.pdf`;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
